import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getMemToken } from "@/api/api";
import {
  Shield, AlertTriangle, Download, Trash2, Activity, Database,
  Users, Building2, Loader2, RefreshCcw, ChevronRight,
} from "lucide-react";
import moment from "moment";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function fetchStats(days) {
  const token = getMemToken();
  const res = await fetch(`${API_URL}/api/rgpd/security-stats?days=${days}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Error carregant estadístiques");
  return res.json();
}

const ACTION_LABEL = {
  rgpd_account_deletion: { label: "Compte eliminat", color: "bg-red-100 text-red-700", icon: Trash2 },
  rgpd_export: { label: "Export RGPD", color: "bg-blue-100 text-blue-700", icon: Download },
  audit_logs_cleanup: { label: "Logs netejats", color: "bg-gray-100 text-gray-700", icon: Activity },
  delete_users: { label: "Usuari eliminat", color: "bg-red-100 text-red-700", icon: Trash2 },
};

export default function SecurityAlertsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async (d) => {
    setLoading(true);
    try {
      setStats(await fetchStats(d));
    } catch {
      setStats(null);
    }
    setLoading(false);
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (loading && !stats) {
    return (
      <Card className="p-5 border-0 shadow-sm" data-testid="security-alerts-panel">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  if (!stats) return null;

  const s = stats.summary;
  const hasAlerts = s.denied_access > 0 || s.account_deletions > 0;

  const kpis = [
    { label: "Accessos denegats", value: s.denied_access, color: "text-red-700", bg: "bg-red-50", icon: AlertTriangle },
    { label: "Exports RGPD", value: s.rgpd_exports, color: "text-blue-700", bg: "bg-blue-50", icon: Download },
    { label: "Comptes eliminats", value: s.account_deletions, color: "text-red-700", bg: "bg-red-50", icon: Trash2 },
    { label: "Backups", value: s.backup_count, color: "text-purple-700", bg: "bg-purple-50", icon: Database },
    { label: "Usuaris", value: s.user_count, color: "text-emerald-700", bg: "bg-emerald-50", icon: Users },
    { label: "Locals", value: s.venue_count, color: "text-amber-700", bg: "bg-amber-50", icon: Building2 },
  ];

  return (
    <Card className={`p-5 border-0 shadow-sm ${hasAlerts ? "ring-1 ring-red-200" : ""}`} data-testid="security-alerts-panel">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${hasAlerts ? "bg-red-50" : "bg-emerald-50"}`}>
            <Shield className={`w-5 h-5 ${hasAlerts ? "text-red-600" : "text-emerald-600"}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Estat de seguretat</h3>
            <p className="text-xs text-gray-500">Últims {days} dies · {s.total_events} esdeveniments registrats</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[1, 7, 30].map((d) => (
            <button
              key={`days-${d}`}
              onClick={() => setDays(d)}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${days === d ? "bg-[#7B2D3F] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid={`security-period-${d}`}
            >
              {d === 1 ? "Avui" : d === 7 ? "7d" : "30d"}
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => load(days)} className="h-7 w-7 p-0" data-testid="security-refresh">
            <RefreshCcw className="w-3.5 h-3.5" />
          </Button>
          <Link to={createPageUrl("RGPDCenter")}>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
              Veure tot <ChevronRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className={`p-2.5 rounded-lg ${k.bg}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-3.5 h-3.5 ${k.color}`} />
                <p className="text-xs text-gray-600 truncate">{k.label}</p>
              </div>
              <p className={`text-xl font-semibold ${k.color}`}>{k.value}</p>
            </div>
          );
        })}
      </div>

      {/* Recent denied access */}
      {stats.recent_denied && stats.recent_denied.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Intents d'accés denegats
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {stats.recent_denied.slice(0, 5).map((d, idx) => (
              <div key={`${d.ts}-${d.user_email}-${idx}`} className="flex items-center justify-between gap-2 text-xs bg-red-50 rounded px-2 py-1">
                <span className="font-mono truncate">{d.user_email}</span>
                <span className="text-gray-500 truncate flex-1 mx-2">→ {d.venue_id}</span>
                <span className="text-gray-500 whitespace-nowrap">{moment(d.ts).fromNow()}</span>
              </div>
            ))}
            {stats.recent_denied.length > 5 && (
              <p className="text-xs text-gray-500 italic pl-2">+{stats.recent_denied.length - 5} més</p>
            )}
          </div>
        </div>
      )}

      {/* Recent sensitive events */}
      {stats.recent_sensitive && stats.recent_sensitive.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-purple-500" /> Esdeveniments sensibles
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {stats.recent_sensitive.slice(0, 5).map((s2, idx) => {
              const meta = ACTION_LABEL[s2.action] || { label: s2.action, color: "bg-gray-100 text-gray-700" };
              return (
                <div key={`${s2.ts}-${idx}`} className="flex items-center justify-between gap-2 text-xs bg-gray-50 rounded px-2 py-1">
                  <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                  <span className="font-mono truncate flex-1 mx-2 text-gray-700">{s2.user_email}</span>
                  <span className="text-gray-500 whitespace-nowrap">{moment(s2.ts).fromNow()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Last backup info */}
      {s.backup_last && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
          <span className="text-gray-500">Últim backup:</span>
          <span className="font-medium text-gray-700">{moment(s.backup_last).format("DD/MM/YYYY HH:mm")} <span className="text-gray-400">· {moment(s.backup_last).fromNow()}</span></span>
        </div>
      )}
    </Card>
  );
}
